import { Brick, registerBrick, Query, ErrorFlow } from 'olympe';

const cleanseQuery = (query) => {
    const pattern = /':p(\d+)'/g;
    return query.replace(pattern, ":p$1");
}

const seperateValueBasedonTableName = (cleanedData) => Object.entries(cleanedData).reduce( (acc, curr) => {

    const key = curr[0];

    if(key !== "#tag" && key !== "#type"){

        curr[1].tableName.forEach( (tableName, index) => {
            
                if(!acc[tableName])
                    acc[tableName] = [];
                    
                acc[tableName].push({
                    key,
                    value : curr[1].value[index]
                })
            }
        )
    }

    return acc;
},{})

const removeIfNotInMapper = ({mapper, data, bindings}) => {

    const finalData = {};

    const createBindings = ({ value, table }) => {

        if(!bindings[table])
            bindings[table] = {
                paramIndex: 0
            }

        const newParam = `p${bindings[table].paramIndex}`;
        const binding = `:${newParam}`;

        bindings[table] = {
            ...bindings[table],
            [newParam]: value,
            paramIndex: bindings[table].paramIndex + 1
        }
    
        return binding;
    };

    const fieldToColumn = (key) => mapper[key];

    Object.keys(mapper).forEach((key) => {

            const column = fieldToColumn(key)?.columnName;
    
            mapper[key].tableNames.forEach(table => {

                if (column && data[key] !== undefined && data[key] !== null) {
                      
                    if(!finalData[column]){

                        finalData[column] = {}
                        finalData[column].tableName = []
                        finalData[column].value = []

                    }

                    const newBinding = createBindings({ value: data[key], table});

                    finalData[column].tableName.push(table)
                    finalData[column].value.push(newBinding)

                        
                } else {
                    delete data[key];
                }
            })

            
    })

    Object.keys(bindings).forEach(tableName => {
        delete bindings[tableName].paramIndex;
    })

    return finalData;
};

const generateUpdateQuery = ({ mapper, updateConditions, data }) => {

    let bindings = []

    const finalConditions = updateConditions.reduce( (acc, curr) => {
        const dbKey = Object.entries(curr)[0][0];
        const dataKey = Object.entries(curr)[0][1];
        acc[dbKey] = data[dataKey];
        return acc;
    },{})

    const cleanedData = removeIfNotInMapper({mapper, data, bindings});

    const seperatedValueBasedonTableName = seperateValueBasedonTableName(cleanedData);

    const queries = Object.keys(seperatedValueBasedonTableName).map( tableName => {

        const updates = seperatedValueBasedonTableName[tableName]
            .map( row => {
                const {key, value} = row;
                if (typeof value === "string") {
                // Escape single quotes in the value
                    return `${key} = '${value.replace(/'/g, "''")}'`;
                } else if (value === null) {
                    return `${key} = NULL`;
                } else {
                    return `${key} = ${value}`;
                }
            }).join(", ");

                // Create the condition string from the conditions object
        const conditionStrings = Object.keys(finalConditions)
            .map((key) => {
                const value = finalConditions[key];
                if (typeof value === "string") {
                // Escape single quotes in the condition value
                return `${key} = '${value.replace(/'/g, "''")}'`;
                } else if (value === null) {
                return `${key} IS NULL`;
                } else {
                return `${key} = ${value}`;
                }
            })
            .join(" AND ");

        return cleanseQuery(`UPDATE ${tableName} SET ${updates} WHERE ${conditionStrings};`);
    })

    // Construct the UPDATE query
    return {
        queries,
        bindings
    }
};

const generateInsertQuery = ({ mapper, data }) => {

    let bindings = []

    const cleanedData = removeIfNotInMapper({mapper, data, bindings});

    const seperatedValueBasedonTableName = seperateValueBasedonTableName(cleanedData);

    const queries = Object.keys(seperatedValueBasedonTableName).map( tableName => {
      
            // Extract column names and values from the data object
            const columnsString = seperatedValueBasedonTableName[tableName].map(({key}) => key).join(', ');

            const valuesString = seperatedValueBasedonTableName[tableName].map(({value}) => {
                if (typeof value === "string") {
                    // Escape single quotes by replacing each ' with ''
                    return `'${value.replace(/'/g, "''")}'`;
                } else if (value === null) {
                    return "NULL";
                } else {
                    return value;
                }
            }).join(', ');

        return cleanseQuery(`INSERT INTO ${tableName} (${columnsString}) VALUES (${valuesString});`);

    });

    // Construct the INSERT query
    return {
        queries,
        bindings
    }
};

const generateQuery = ({modelTag, updateConditions, payload}) => {
 
    const { toDelete, toInsert, toUpdate } = payload;
    
    const paramConfigs = Query.instancesOf(modelTag, false, "server").executeFromCache();

    const mapper = paramConfigs.reduce((acc, curr) => {
            const column = curr.get("0185c081050d996c424c");
            const field = curr.get("0185c081098c285960d8");
            const tableName = `${curr.get('01862c935347456d04b2')}.${curr.get('0185c080fff9d4533458')}`

            if(!acc[field] && !Array.isArray(acc[field]?.tableNames)){
                acc[field] = {}
                acc[field].tableNames = [];
            }

            acc[field] = {
                tableNames: [...acc[field].tableNames, tableName],
                columnName: `${column}`
            }
        return acc;
    }, {});


    const toUpdateMerged = [...(Array.isArray(toDelete) ? toDelete : []), ...(Array.isArray(toUpdate) ? toUpdate : [])];
    const insertItemsCount = toInsert?.length || 0; 
    const updateItemsCount = toUpdateMerged?.length || 0;

    const insertQueries = insertItemsCount > 0 ? toInsert
        .map((item) => {
            const {queries, bindings: bindingsObject} = generateInsertQuery({ mapper, data: item });
            const bindings = Object.values(bindingsObject);

            return queries.map( (query, index) => ({
                query,
                bindings: bindings[index]
            }))

        }) : []


    const updateQueries = updateItemsCount > 0 ? toUpdateMerged
        .map((item) => {
            const {queries, bindings: bindingsObject} = generateUpdateQuery({ mapper, updateConditions, data: item })
            const bindings = Object.values(bindingsObject);

            return queries.map( (query, index) => ({
                query,
                bindings: bindings[index]
            }))

        }) : [];

    return [...insertQueries.flat(),...updateQueries.flat()]
}

export default class BuildTransactionSQLQueryV3 extends Brick {

    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {ListDef} modelTags
     * @param {*} payload
     * @param {function()} forwardEvent
     * @param {function(string)} setQuery
     * @param {function(*)} setBindings
     * @param {function(*)} setErrorFlow
     */
    update($, [modelTags, payload, updateConditions], [forwardEvent, setQuery, setErrorFlow]) {
      
        try {
         
            const queries = payload.reduce( (acc, curr, index) => {
                    const newQuery = generateQuery({modelTag: modelTags[index], updateConditions:  updateConditions ? updateConditions[index] || [] : [], payload: curr} );
                    return [...acc ,...newQuery]     
                },[]
            )
           
            setQuery(queries);
            forwardEvent();

        } catch (error) {

            setErrorFlow(ErrorFlow.create(`Error ${error} during the execution`));

        }
    }
}

registerBrick('0193cfd60fe5263e4ac5', BuildTransactionSQLQueryV3);
